import React, {useCallback, useMemo, useState, useRef, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import styled from 'styled-components';
import EllipsesControl from '../EllipsesControl/EllipsesControl';
import Modal from '../Modal/Modal';
import RelateItemList from '../RelateList/RelateItemList';
import {labels, currentResource, isNullOrUndefined, isNullOrWhitespace} from '../../../util/utils';
import {modalLevelToast, specialBodyExtendedPagingOptions} from '../../../util/uiconstants';
import {useDocumentId, useEngagementId} from '../../../util/customHooks';
import {getAllItApplications} from '../../../actions/ITApplication/itapplicationaction';
import {
	relateItApplicationToFlowChartStep,
	unrelateItApplicationFromFlowChartStep,
	updateStoreRelatedFlowchartStepITApplication,
	updateStoreUnrelatedITApplicationFromFlowchartStep
} from '../../../actions/FlowChart/flowchartstepactions';
import {clearErrors} from '../../../actions/erroractions';
import {setFormV2ToasterVisibility} from '../../../actions/toastactions';

export default function RelateFlowchartITAppSOModal({isModalOpen, onClickRelateModal, isITApplication, stepName}) {
	const dispatch = useDispatch();
	const entityUIDInt = useSelector((state) => state.flowChart?.data[0]?.entityUIDInt);

	const flowchartEntities = useRef(
		new Set(
			useSelector((state) => {
				if (isITApplication) {
					return (state.flowchartStep.flowchartStepsITApplication || [])?.map((entity) => entity.itApplicationId);
				} else {
					return (state.flowchartStep.flowchartStepsSO || [])?.map((entity) => entity.itApplicationId);
				}
			})
		)
	);

	const flowchartId = useSelector((state) => state.flowChart.data[0].id);
	const flowchartStep = useSelector((state) => state.flowchartStep);
	const engagementId = useEngagementId();
	const documentId = useDocumentId();
	const [page, setPage] = useState(1);

	useEffect(() => {
		dispatch(clearErrors());
		dispatch(setFormV2ToasterVisibility(false));
	}, []);

	const onPageChanged = useCallback((page) => {
		setPage(page);
	}, []);

	const onPageSizeChanged = useCallback(() => {
		setPage(1);
	}, []);

	const selected = useCallback(
		(entity) => {
			return flowchartEntities.current.has(entity.id);
		},
		[flowchartEntities]
	);

	const renderItemComponent = useCallback((entity) => {
		return <EllipsesControl noOfLines={1} content={entity.itApplicationName} tooltip={entity.itApplicationName} />;
	}, []);

	const componentHeader = useCallback(() => {
		return (
			<section className="filter-itso-container">
				<section className="related-itso">
					<EllipsesControl noOfLines={1} content={stepName} tooltip={stepName} />
				</section>
			</section>
		);
	}, [stepName]);

	const relateListHeaders = useCallback(
		(totalEntityCount) => (
			<section className="itso-header">
				<section className="itso-name">
					{isITApplication ? labels.flowChartITApplication.columnName : labels.flowChartITSO.columnName}
				</section>
				<section className="itso-count">
					{totalEntityCount > 0 && (
						<section>
							{`${
								totalEntityCount > 1
									? isITApplication
										? labels.flowChartITApplication.itApplicationsCounter
										: labels.flowChartITSO.itSOsCounter
									: isITApplication
									? labels.flowChartITApplication.itApplicationCounter
									: labels.flowChartITSO.itSOCounter
							}`.replace('{0}', totalEntityCount)}
						</section>
					)}
				</section>
			</section>
		),
		[isITApplication]
	);

	const data = useCallback(
		(page, pageSize, searchText, sortColumn, selected) => {
			let queryString = {
				page: page,
				pageSize: pageSize,
				includePagination: true,
				filterITApplicationType: isITApplication ? 1 : 2,
				filterRelatedSCOT: entityUIDInt
			};
			if (!isNullOrWhitespace(searchText)) {
				queryString.searchString =
					isNullOrWhitespace(searchText) && isNullOrUndefined(searchText) ? '' : searchText.trim();
			}
			if (selected) {
				queryString.filterRelatedFlowchartStep = flowchartStep.id;
			}
			return dispatch(getAllItApplications(engagementId, {resolve: true}, queryString));
		},
		[dispatch, engagementId, entityUIDInt, flowchartStep.id, isITApplication]
	);
	const onCheckChange = useCallback(
		(itApplicationItem, checked) => {
			let promise = null;
			if (checked) {
				let ItAppData = {
					itApplicationId: itApplicationItem.id,
					documentId: documentId
				};
				promise = dispatch(
					relateItApplicationToFlowChartStep(engagementId, flowchartId, flowchartStep.id, ItAppData)
				).then(() => {
					flowchartEntities.current.add(itApplicationItem.id);
					dispatch(
						updateStoreRelatedFlowchartStepITApplication({
							itApplicationName: itApplicationItem.itApplicationName,
							engagementId: engagementId,
							flowchartStepId: flowchartStep.id,
							isServiceOrganization: itApplicationItem.isServiceOrganization,
							itApplicationId: itApplicationItem.id,
							remove: {}
						})
					);
				});
			} else {
				promise = dispatch(
					unrelateItApplicationFromFlowChartStep(engagementId, flowchartId, flowchartStep.id, itApplicationItem.id)
				).then(() => {
					flowchartEntities.current.delete(itApplicationItem.id);
					dispatch(
						updateStoreUnrelatedITApplicationFromFlowchartStep({
							flowchartStepId: flowchartStep.id,
							itApplicationId: itApplicationItem.id,
							isServiceOrganization: itApplicationItem.isServiceOrganization
						})
					);
				});
			}
			return promise;
		},
		[dispatch, engagementId, flowchartId, flowchartStep.id, documentId, flowchartEntities]
	);

	let configuration = useMemo(
		() => ({
			noResultsLabel: labels.flowchartITAPPSO.noResultsFound,
			data: (page, pageSize, searchString, sortColumn, selected) =>
				data(page, pageSize, searchString, sortColumn, selected),
			selected: (entity) => selected(entity),
			mapResponse: (response) => mapResponse(response),
			render: renderItemComponent,
			onCheckBoxChange: onCheckChange,
			noDataLabel: isITApplication ? labels.flowChartITApplication.noDataFound : labels.flowChartITSO.noDataFound
		}),
		[]
	);

	const mapResponse = (response) => {
		if (response) {
			let entityList = response.data && response.data.length > 0 ? response.data : [];
			return {
				page: response.page,
				pages: response.pages,
				totalEntityCount: response.totalEntityCount,
				data: entityList
			};
		}
	};

	return (
		<>
			{
				<Modal
					modalsize="medium"
					show={isModalOpen}
					title={isITApplication ? labels.flowChartITApplication.headerName : labels.flowChartITSO.headerName}
					onHide={onClickRelateModal}
					closeTitle={labels.cancelLabel}
					showFooter={false}
					modalLevel={modalLevelToast.level2}
				>
					<StyledSection>
						<RelateItemList
							key={entityUIDInt}
							componentHeader={componentHeader}
							listHeaders={relateListHeaders}
							search
							pagination
							optionsPerPage={specialBodyExtendedPagingOptions.options}
							configuration={configuration}
							showAddedLabel
							addedLabel={labels.added}
							searchPlaceholder={labels.searchPlaceholder}
							paginationIconHover={currentResource.pagingSvgHoverText}
							paginationShowText={labels.pagingShowtext}
							showSelectedOnlyCheckBox
							showSelectedOnlyText={labels.flowchartITAPPSO.showOnlyRelatedText}
							page={page}
							onPageChanged={onPageChanged}
							onPageSizeChanged={onPageSizeChanged}
							onSearch={() => setPage(1)}
							onFilter={() => setPage(1)}
						/>
					</StyledSection>
				</Modal>
			}
		</>
	);
}

const StyledSection = styled.section`
	height: 100%;

	.relate-list-body-wrapper {
		.relate-item-list-labelTxt {
			width: 85%;
			max-width: 85%;
		}
	}

	.RelateItemsListHeaderSelectedOnly {
		max-width: 100%;
		display: flex;
		align-items: center;
		.RelateItemsListHeaderSelectedOnlyCheckbox {
			margin: var(--px-10) 0;
			max-width: 100%;
		}
		.RelateItemsListHeaderSelectedOnlyCheckbox {
			.motif-checkbox-label {
				width: calc(100% - var(--px-20));
			}
		}
	}
	.RelateItemsListHeaderComponent {
		width: 100% !important;
		max-width: 80%;
	}
	.filter-itso-container {
		width: 100%;
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: var(--px-10) 0;
		.related-itso {
			display: flex;
			width: 100%;
			font-size: var(--px-14);
			font-weight: 300;
			color: var(--neutrals-900);

			.title {
				margin-right: var(--px-4);
				font-size: var(--px-14);
				font-weight: 300;
			}

			.ellipses {
				font-size: var(--px-14);
				font-weight: 400;
				width: 92%;
			}
		}
	}
	.itso-name {
		margin-left: var(--px-33);
		font-weight: bold;
	}
	.itso-count {
		margin-left: auto;
		margin-right: var(--px-20);
		min-width: var(--px-100);
		text-align: right;
	}
	.itso-header {
		display: flex;
	}
	.motif-checkbox {
		display: flex;
		justify-content: flex-end;
		width: 100%;
	}
`;
